import { WriteTransaction } from 'replicache'
import { updateBlock } from './crud'
import {
  isBlockWithChildren,
  isComponentBlock,
  isInstanceBlock,
  Block,
  isListBlock,
  isRichTextBlock,
  shouldInheritHiddenState,
  isReferenceBlock,
  blockRelationshipBaseOps,
  BlockRelationship,
  RootBlock,
  isStructuralBr,
  isVirtualInstanceChildTarget,
  isBranch,
} from '@basehub/data/replicache/index'
import { validateBlockNestingPromise } from '../helpers/component-helpers'
import cloneDeep from 'lodash/cloneDeep'
import { propagateHiddenStateToChildren } from './hidden'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { invariant } from '@epic-web/invariant'
import { WithServerSecret } from '@basehub/data/replicache/tx/spec'
import { hasAccessOrThrow } from '@basehub/data/replicache/mutators/team-space/team/auth'
import { idToSourceBlockId, simpleCopyOnWrite } from './copy-on-write'
import { BlockBatchMutationParams } from './batch'
import {
  generateKeyBetween,
  generateNKeysBetween,
} from '@basehub/data/replicache/mutators/repo-space/block/helpers/fractional-indexing'
import pMap from 'p-map'
import {
  blockBaseOps,
  ensureBaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import { TreeNode } from '@basehub/utils/tree/index'

/* -------------------------------------------------------------------------------------------------
 * Reorder Blocks
 * -----------------------------------------------------------------------------------------------*/

export type ReorderBlockParams = {
  rootId: string
  blockIds: string[]
  targetBlockId: string
  intendedIndex: number
  isoNow: string
  idSeed: string
}

export async function reorderBlocks(
  tx: WriteTransaction,
  {
    rootId,
    blockIds,
    targetBlockId: newParentId,
    intendedIndex,
    isoNow,
    idSeed,
  }: WithServerSecret<ReorderBlockParams>
) {
  ensureBaseHubTransaction(tx)
  // 1. build tree

  if (tx.serverOnly?.playgroundId) {
    // rewrite rootId to the playground working root (hacky but JUST for playgrounds)
    if (
      tx.serverOnly.resolvedRef?.value &&
      isBranch(tx.serverOnly.resolvedRef.value) &&
      tx.serverOnly.resolvedRef.value.workingRootBlockId
    ) {
      rootId = tx.serverOnly.resolvedRef.value.workingRootBlockId
    }
  }

  const treeManager = await tx.getTreeManager(rootId)
  const rootBlock = treeManager.root.value as RootBlock
  const brs = await tx.getBrsByRootId(rootBlock)
  const newParentNode = treeManager.getNode(newParentId, { required: true })
  const newParentBlock = newParentNode.value
  invariant(newParentBlock, `newParentBlock ${newParentId} not found`)
  const newParentPath = treeManager.getPath(newParentBlock.id, {
    format: 'string',
    required: true,
  })
  let newParentChildrenKeys = treeManager.getChildrenKeys(newParentBlock.id)

  if (isListBlock(newParentBlock)) {
    // filter out non visible template component
    newParentChildrenKeys = newParentChildrenKeys.filter((key) => {
      const node = treeManager.getNode(key, { required: true })
      if (isComponentBlock(node.value)) {
        return !node.value.hidden
      }
      return true
    })
  }

  const lowerBound = newParentChildrenKeys[intendedIndex - 1]
  const upperBound = newParentChildrenKeys[intendedIndex]

  const lowerBoundBr = lowerBound
    ? await tx.getBlockRelationshipByBlockId({
        blockId: lowerBound,
        rootBlockId: rootId,
      })
    : undefined
  const upperBoundBr = upperBound
    ? await tx.getBlockRelationshipByBlockId({
        blockId: upperBound,
        rootBlockId: rootId,
      })
    : undefined

  const newOrders = generateNKeysBetween(
    lowerBoundBr?.order ?? null,
    upperBoundBr?.order ?? null,
    blockIds.length,
    rootId
  )

  /** Validate the block nesting before reordering */
  switch (newParentBlock.type) {
    case 'instance':
      // error
      if (isInstanceBlock(newParentBlock)) {
        throw new MutatorUserError(
          `targetNode ${newParentBlock.id} is an instance block. Instance blocks can't have new blocks.`
        )
      }
      break
    case 'list': {
      // only allow instances of the list type
      const listTargetBlockId = newParentBlock.typeOptions.targetBlockId
      let allGood = true
      const p = blockIds.map(async (bId) => {
        const node = treeManager.getNode(bId)
        if (
          node?.value.type === 'instance' &&
          node.value.typeOptions.targetBlockId === listTargetBlockId
        ) {
          return // cool
        }
        if (
          node?.value.type === 'component' &&
          (node.value.sourceBlockId || node.value.id === listTargetBlockId)
        ) {
          return // cool
        }
        return (allGood = false)
      })
      await Promise.all(p)
      if (!allGood) {
        throw new MutatorUserError(
          `targetNode ${newParentBlock.id} is a list block. Only instances of the list type are allowed as children.`
        )
      }
      break
    }
    case 'reference': {
      // only allow instances of the list type
      let allGood = true
      const p = blockIds.map(async (bId) => {
        invariant(
          isReferenceBlock(newParentBlock),
          'targetNode should be a union block'
        )
        const node = treeManager.getNode(bId)
        if (
          (node?.value.type === 'instance' &&
            newParentBlock.constraints.acceptedTypes?.includes(
              node.value.typeOptions.targetBlockId
            )) ||
          (node?.value.type === 'component' &&
            newParentBlock.constraints.acceptedTypes?.includes(
              node.value.sourceBlockId || node.value.id
            ))
        ) {
          return // cool
        }
        return (allGood = false)
      })
      await Promise.all(p)
      if (!allGood) {
        throw new MutatorUserError(
          `targetNode ${newParentBlock.id} is a union block. Only instances of the allowed types are allowed as children.`
        )
      }

      break
    }
    case 'rich-text': {
      throw new MutatorUserError(
        `targetNode ${newParentBlock.id} is a rich-text block. Rich text children can not be reordered`
      )
    }
    case 'component': {
      // check valid block nesting for dropped instances
      const validateBlockNestingRecursively = async (
        bId: string,
        path: string
      ): ReturnType<typeof validateBlockNestingPromise> => {
        const node = treeManager.getNode(bId, {
          required: true,
        })
        const result = await validateBlockNestingPromise({
          block: node.value,
          path,
          shouldThrowIfInvalid: false,
          fetchBlock: (id) => blockBaseOps.get(tx, id),
          fetchChildren: async (sourceBlockId) => {
            return treeManager
              .getChildrenKeys(sourceBlockId)
              .map((key) => treeManager.getNode(key, { required: true }).value)
          },
        })
        if (!result.isValid) return result
        for (const childId of treeManager.getChildrenKeys(bId)) {
          const childNode = treeManager.getNode(childId, {
            required: true,
          })
          if (!childNode) throw new Error(`Child node ${childId} not found`)
          const childResult = await validateBlockNestingRecursively(
            childNode.value.id,
            path + '/' + childNode.value.id
          )
          if (!childResult.isValid) return childResult
        }
        return { isValid: true }
      }

      const rootPath = treeManager.getPath(newParentBlock.id, {
        format: 'string',
        required: true,
      })

      await Promise.all(
        blockIds.map(async (bId) => {
          const result = await validateBlockNestingRecursively(
            bId,
            rootPath + '/' + bId
          )
          if (!result.isValid) throw new MutatorUserError(result.reason)
        })
      )

      break
    }
    default:
      // should be OK
      break
  }
  /** Done validating block nesting */

  const undo: BlockBatchMutationParams['mutations'] = []
  const byInstanceChildTargetIndex = treeManager.compoundIndexes.get(
    'byInstanceChildTarget'
  )
  const oldNonVirtualInstanceChildren = new Map<
    string,
    { instanceChildNode: TreeNode<Block>; path: string[] }[]
  >()

  // 2. iterate through all blockIds, get their parents, and strip the child from them
  // old parents should be saved for rehashing
  await pMap(blockIds, async (id, i) => {
    let undoBrChange: BlockBatchMutationParams['mutations'][number] | null =
      null
    const newOrder = newOrders[i]
    invariant(newOrder, `newOrder ${i} not found`)
    const node = treeManager.getNode(id, { required: true })
    const block = node.value
    const oldParentNode = node.parentKey
      ? treeManager.getNode(node.parentKey, { required: true })
      : undefined
    const oldParent = oldParentNode?.value
    const br = brs.get(idToSourceBlockId(block))

    invariant(block, `block ${id} not found`)
    invariant(oldParent, `oldParent ${node.parentKey} not found`)
    invariant(br, `br ${id} of block ${id} not found`)
    const sourceBlockId = idToSourceBlockId(block)

    const instanceChildren = byInstanceChildTargetIndex?.get(
      idToSourceBlockId(node.value)
    )

    if (instanceChildren?.size) {
      oldNonVirtualInstanceChildren.set(
        idToSourceBlockId(node.value),
        Array.from(instanceChildren)
          .map((c) => {
            const n = treeManager.getNode(c, { required: true })
            if (isVirtualInstanceChildTarget(n.value)) return null
            return {
              instanceChildNode: n,
              path: treeManager.getPath(n.value.id, {
                format: 'array',
                required: true,
              }),
            }
          })
          .filter(Boolean)
      )
    }

    /** Editors should be able to reorder:
     * - Instances or components inside collections
     * - Instances or components inside unions
     */

    if (
      (newParentBlock.type === 'list' || newParentBlock.type === 'reference') &&
      (oldParent.type === 'reference' || oldParent.type === 'list')
    ) {
      hasAccessOrThrow({
        serverOnly: tx.serverOnly,
        clientOnly: tx.clientOnly,
        serverSecret: tx.serverSecret,
        allowedRoles: ['owner', 'member', 'editor'],
        guestAccess: 'branch-author',
      })
    } else {
      hasAccessOrThrow({
        serverOnly: tx.serverOnly,
        clientOnly: tx.clientOnly,
        serverSecret: tx.serverSecret,
        allowedRoles: ['owner', 'member'],
        guestAccess: 'branch-author',
      })
    }

    // check if we're trying to reorder out of an instance block
    if (isInstanceBlock(oldParent)) {
      throw new Error(
        `Can't reorder child ${id} of instance block ${oldParent.id}`
      )
      // check if we're trying to reorder an instance block out of its rich text block
    } else if (isRichTextBlock(oldParent) && isInstanceBlock(block)) {
      throw new Error(
        `Can't reorder instance block ${block.id} out of a rich text block ${oldParent.id}`
      )
    }

    let newBr: BlockRelationship = br
    if (br.isCommitted || br.branchId !== rootBlock.branchId) {
      // create a new br (lil copy-on-write)
      newBr = simpleCopyOnWrite({
        brOrBlock: br,
        rootBlock: treeManager.root.value as RootBlock,
        isoNow,
        idSeed,
        extras: {
          parentId: idToSourceBlockId(newParentBlock),
          structural: isStructuralBr({ parent: newParentBlock, block }),
          order: newOrder,
          ...(rootBlock.isInlineSuggestion ? { isInlineSuggestion: true } : {}),
        },
      })
      await blockRelationshipBaseOps.set(tx, newBr)
      treeManager.updateNode(node.key, {
        parentKey: newBr.parentId,
        order: newBr.order,
      })
      undoBrChange = {
        op: 'updateBR',
        args: {
          id: newBr.id,
          parentId: br.parentId,
          order: br.order,
        },
      }
    } else {
      // update the existing br
      const updated = {
        ...br,
        parentId: idToSourceBlockId(newParentBlock),
        structural: isStructuralBr({ parent: newParentBlock, block }),
        order: newOrder,
        updatedAt: isoNow,
      }
      await blockRelationshipBaseOps.update(tx, updated)
      treeManager.updateNode(node.key, {
        parentKey: updated.parentId,
        order: updated.order,
      })
      undoBrChange = {
        op: 'updateBR',
        args: {
          id: br.id,
          parentId: br.parentId,
          order: br.order,
        },
      }
      newBr = updated
    }

    if (isBlockWithChildren(oldParent)) {
      const isDraggingToParent =
        idToSourceBlockId(oldParent) === idToSourceBlockId(newParentBlock)
      const shouldInheritHiddenStateResult = shouldInheritHiddenState(
        block,
        newParentPath,
        (id) =>
          treeManager.getNode(id, {
            fallbackKey: 'sourceBlockId',
            required: true,
          }).value
      )

      const shouldAutoUnHide =
        !shouldInheritHiddenStateResult &&
        !isDraggingToParent &&
        // template component was reorder out of list
        // so we un-hide it
        isComponentBlock(block) &&
        isListBlock(oldParent) &&
        oldParent.typeOptions.targetBlockId === sourceBlockId

      const shouldAutoHide =
        !shouldInheritHiddenStateResult &&
        !isDraggingToParent &&
        // template component was reorder INTO a list
        // so now we hide it
        isComponentBlock(block) &&
        isListBlock(newParentBlock) &&
        newParentBlock.typeOptions.targetBlockId === sourceBlockId

      if (
        shouldAutoHide ||
        shouldAutoUnHide ||
        shouldInheritHiddenStateResult
      ) {
        const blockClone = cloneDeep(block)

        // we alter it, and take care of its hash, directly.
        // we set it updated into `draggedBlocksMap`, so that's how it'll be saved later.
        blockClone.hidden = shouldInheritHiddenStateResult
          ? newParentBlock.hidden
          : shouldAutoHide
            ? true
            : false
        const shouldPin = shouldAutoHide && blockClone.hidden === true

        if (blockClone.hidden !== block.hidden) {
          if (isBlockWithChildren(blockClone)) {
            const result = await propagateHiddenStateToChildren(tx, {
              block: blockClone,
              dryRun: true, // cause we'll update on the next line
              isoNow,
              path: treeManager.getPath(blockClone.id, {
                format: 'string',
                required: true,
              }),
              rootBlockId: rootId,
            })
            undo.push(...result.undo)
          }

          const { undo: undoForThis } = await updateBlock(tx, {
            id: blockClone.id,
            data: { type: blockClone.type, hidden: blockClone.hidden },
            instanceChildTarget: blockClone.instanceChildTarget,
            path: treeManager.getPath(blockClone.id, {
              format: 'string',
              required: true,
            }),
            strategy: 'merge',
            isoNow,
            skipPropagateHiddenState: true,
          })
          undo.push(...undoForThis)

          if (shouldPin) {
            const children = await tx.getBlockChildren({
              sourceBlockId: idToSourceBlockId(newParentBlock),
              rootBlockId: rootId,
            })
            const firstChild = children[0]
            invariant(firstChild, 'no children found')
            if (firstChild.id !== newBr.blockId) {
              const firstChildBr = await blockRelationshipBaseOps.get(
                tx,
                firstChild.id
              )
              invariant(firstChildBr, 'no first child br found')
              const newOrder = generateKeyBetween(
                null,
                firstChildBr.order,
                rootId
              )
              await blockRelationshipBaseOps.update(tx, {
                id: newBr.id,
                order: newOrder,
              })
              treeManager.updateNode(newBr.blockId, {
                order: newOrder,
              })
            }
          }
        }
      }
    }

    tx.revalidateBlockMilestonesMap.set(block.id, {
      path: treeManager.getPath(newParentBlock.id, { ignoreSelf: false }),
      sourceBlockId: idToSourceBlockId(block),
      rootId,
      isoNow,
      blockId: block.id,
      isPropagating: null,
    })

    if (undoBrChange) {
      undo.push(undoBrChange)
    }
  })

  // 3. get targetNode (new parent) and set the children to the `blockIds`, at their intended index
  // save new parent for rehashing
  const dropTargetBlock = newParentBlock
  const targetBlock = cloneDeep(dropTargetBlock)

  if (!isBlockWithChildren(targetBlock)) {
    throw new Error(
      `dropTargetBlock should be a blockWithChildren, instead it is: ${newParentBlock.type}`
    )
  }

  if (isComponentBlock(targetBlock)) {
    // is reordering into a component
    // we should check if there are instances that have a block that was reordered into it.
    // if there are, and they have an instance of this component nested in them, we can reorder
    // that child instance below it. it's a nice little detail.
    const processInstanceChildrenToFindNewInstanceForInstanceChild = (
      instanceNode: TreeNode<Block>
    ): TreeNode<Block> | undefined => {
      if (!isInstanceBlock(instanceNode.value)) return undefined
      if (
        instanceNode.value.typeOptions.targetBlockId ===
        idToSourceBlockId(targetBlock)
      ) {
        return instanceNode
      }
      const children = treeManager.getChildrenKeys(instanceNode.value.id)
      for (const child of children) {
        const childNode = treeManager.getNode(child, { required: true })
        if (isInstanceBlock(childNode.value)) {
          const found =
            processInstanceChildrenToFindNewInstanceForInstanceChild(childNode)
          if (found) return found
        }
      }
      return undefined
    }

    /** Find "lost" instance children and attempt to reorder them to the right place. */
    await pMap(blockIds, async (id) => {
      const droppedNode = treeManager.getNode(id, { required: true })

      for (const {
        instanceChildNode,
        path,
      } of oldNonVirtualInstanceChildren.get(
        idToSourceBlockId(droppedNode.value)
      ) ?? []) {
        let firstInstance: TreeNode<Block> | undefined
        for (const id of [...path].reverse()) {
          if (id === instanceChildNode.value.id) continue
          const n = treeManager.getNode(id, { required: true })
          if (!isInstanceBlock(n.value)) {
            // stop if path of instances gets "broken"
            break
          }
          firstInstance = n
        }

        if (!firstInstance) continue
        const foundNewParent =
          processInstanceChildrenToFindNewInstanceForInstanceChild(
            firstInstance
          )
        if (!foundNewParent) continue
        if (foundNewParent.key === instanceChildNode.parentKey) continue
        // cool, re-parent it
        const br = await tx.getBlockRelationshipByBlockId({
          blockId: instanceChildNode.value.id,
          rootBlockId: rootId,
        })
        invariant(
          br,
          `br for block ${instanceChildNode.value.id} and root ${rootId} not found`
        )
        await blockRelationshipBaseOps.update(tx, {
          id: br.id,
          parentId: idToSourceBlockId(foundNewParent.value),
          order: droppedNode.order,
        })
        treeManager.updateNode(instanceChildNode.key, {
          parentKey: foundNewParent.key,
          order: droppedNode.order,
        })
      }
    })
  }

  // done
  return { undo }
}
